import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, startWith, tap } from 'rxjs';

/**
 * Directive that provides USA phone number input masking with smart formatting.
 *
 * Features:
 * - Visual format: (123) 456-7890
 * - Only numeric input allowed (0-9)
 * - Smart backspace deletes actual digits, not formatting characters
 * - Form control stores formatted value: "(123) 456-7890"
 * - Maximum 10 digits (US phone numbers)
 * - Automatically formats programmatic value changes (patchValue/setValue)
 *
 * @example
 * ```html
 * <ui-input
 *   formControlName="phoneNumber"
 *   label="Phone Number"
 *   placeholder="(123) 456-7890"
 *   appPhoneNumberMask />
 * ```
 */
@Directive({
  selector: '[appPhoneNumberMask]',
  host: {
    '(input)': 'onInput($event)',
    '(keydown)': 'onKeyDown($event)',
  },
})
export class PhoneNumberMaskDirective implements AfterViewInit {
  private _control = inject(NgControl, { optional: true });
  private _elementRef = inject(ElementRef<HTMLInputElement>);
  private _destroyRef = inject(DestroyRef);

  private _isUserTyping = false;
  private readonly _specialKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ];

  private readonly _combinationKeys = ['a', 'c', 'v', 'x']; // For Ctrl/Cmd + A/C/V/X

  public ngAfterViewInit(): void {
    this._setupProgrammaticFormatting();
  }

  private _setupProgrammaticFormatting(): void {
    const control = this._control?.control;
    if (!control) return;

    const input = this._elementRef.nativeElement;

    control.valueChanges
      .pipe(
        startWith(control.value),

        filter(() => !this._isUserTyping),

        filter((value) => !!value),

        map((value: string) => value.replace(/\D/g, '').substring(0, 10)),

        distinctUntilChanged(),

        map((digits) => this.formatPhoneNumber(digits)),

        distinctUntilChanged(),

        tap((formatted) => this._updateInputAndControl(input, formatted)),

        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  /**
   * Updates both the input element and form control value without triggering infinite loops
   */
  private _updateInputAndControl(
    input: HTMLInputElement,
    formatted: string,
  ): void {
    if (input.value !== formatted) {
      input.value = formatted;
      // Update control without triggering valueChanges again (prevents infinite loop)
      this._control?.control?.setValue(formatted, { emitEvent: false });
    }
  }

  protected onInput(event: Event): void {
    this._isUserTyping = true;

    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const oldValue = input.value;

    // Get raw value and extract only digits (max 10)
    const rawDigits = oldValue.replace(/\D/g, '').substring(0, 10);

    // Format for display
    const formattedValue = this.formatPhoneNumber(rawDigits);

    // Only update if value changed to avoid infinite loops
    if (formattedValue !== oldValue) {
      // Update input display value
      input.value = formattedValue;

      // Calculate new cursor position (after last typed digit)
      const newCursorPos = this.calculateCursorPosition(
        rawDigits,
        oldValue,
        cursorPosition,
      );
      input.setSelectionRange(newCursorPos, newCursorPos);

      // Dispatch new input event to update form control with formatted value
      // This allows ui-input component to handle the form control update
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Reset flag after microtask to allow valueChanges to emit
    Promise.resolve().then(() => {
      this._isUserTyping = false;
    });
  }

  protected onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const key = event.key;
    const cursorPosition = input.selectionStart || 0;

    // Allow Ctrl/Cmd + A, C, V, X
    if (event.ctrlKey || event.metaKey) {
      if (this._combinationKeys.includes(key.toLowerCase())) {
        return;
      }
    }

    // Allow special keys
    if (this._specialKeys.includes(key)) {
      if (key === 'Backspace') {
        this.handleSmartBackspace(event, input, cursorPosition);
      }
      return;
    }

    // Allow only numeric keys
    if (!/^[0-9]$/.test(key)) {
      event.preventDefault();
      return;
    }

    const currentDigits = input.value.replace(/\D/g, '');
    const hasSelection = (input.selectionEnd || 0) > cursorPosition;

    if (currentDigits.length >= 10 && !hasSelection) {
      event.preventDefault();
      return;
    }
  }

  /**
   * Handles smart backspace behavior - deletes digits, not formatting characters
   */
  private handleSmartBackspace(
    event: KeyboardEvent,
    input: HTMLInputElement,
    cursorPos: number,
  ): void {
    const value = input.value;

    // If cursor is right after a formatting character, we need to handle it
    if (cursorPos > 0) {
      const charBeforeCursor = value[cursorPos - 1];

      // If character before cursor is a formatting char, skip it
      if (['(', ')', ' ', '-'].includes(charBeforeCursor)) {
        event.preventDefault();

        // Get raw digits
        const rawDigits = value.replace(/\D/g, '');

        // Calculate which digit to remove based on cursor position
        const digitsBeforeCursor = value
          .substring(0, cursorPos)
          .replace(/\D/g, '').length;

        // Remove that digit
        const newRawDigits =
          rawDigits.substring(0, digitsBeforeCursor - 1) +
          rawDigits.substring(digitsBeforeCursor);

        // Format and update display
        const formatted = this.formatPhoneNumber(newRawDigits);
        input.value = formatted;

        // Calculate new cursor position
        const newCursorPos = this.calculateCursorPositionAfterDelete(
          newRawDigits,
          digitsBeforeCursor - 1,
        );
        input.setSelectionRange(newCursorPos, newCursorPos);

        // Dispatch new input event to update form control
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }

  /**
   * Formats raw digits into US phone number format: (123) 456-7890
   */
  private formatPhoneNumber(rawDigits: string): string {
    if (!rawDigits) return '';

    const length = rawDigits.length;

    if (length <= 3) {
      return rawDigits;
    } else if (length <= 6) {
      return `(${rawDigits.substring(0, 3)}) ${rawDigits.substring(3)}`;
    } else {
      return `(${rawDigits.substring(0, 3)}) ${rawDigits.substring(3, 6)}-${rawDigits.substring(6, 10)}`;
    }
  }

  /**
   * Calculates the correct cursor position after formatting
   */
  private calculateCursorPosition(
    rawDigits: string,
    oldValue: string,
    oldPos: number,
  ): number {
    // Format the number
    const formatted = this.formatPhoneNumber(rawDigits);

    // Count digits before old cursor position in old value
    const digitsBeforeOldPos = oldValue
      .substring(0, oldPos)
      .replace(/\D/g, '').length;

    // Find position in formatted string after those same number of digits
    let digitsSeen = 0;
    let newPos = 0;

    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        digitsSeen++;
        if (digitsSeen === digitsBeforeOldPos) {
          newPos = i + 1;
          break;
        }
      }
    }

    // If we've seen all digits, cursor goes to end
    if (digitsSeen === rawDigits.length || newPos === 0) {
      newPos = formatted.length;
    }

    return newPos;
  }

  /**
   * Calculates cursor position after a delete operation
   */
  private calculateCursorPositionAfterDelete(
    rawDigits: string,
    digitPosition: number,
  ): number {
    const formatted = this.formatPhoneNumber(rawDigits);

    // Find the position after 'digitPosition' digits
    let digitsSeen = 0;
    let pos = 0;

    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        if (digitsSeen === digitPosition) {
          pos = i;
          break;
        }
        digitsSeen++;
      }
    }

    if (digitsSeen < digitPosition) {
      pos = formatted.length;
    }

    return pos;
  }
}
